import { supabase, invokeEdgeFunction, logger, deleteMemberPhotosCompletely, deleteMemberFilesCompletely } from '@/lib';
import { Member } from '@/types/member';

/**
 * Anonymizes a member's personally identifiable information (PII)
 * while preserving business-critical data like date_of_birth
 */
export function anonymizeMemberData(member: Member): Partial<Member> {
  const now = new Date().toISOString();
  // Generate unique email using member ID to avoid duplicate key violations
  const uniqueDeletedEmail = `deleted_member_${member.id}@deleted.local`;

  return {
    first_name: 'Deleted',
    last_name: 'Member',
    email: uniqueDeletedEmail,
    deleted_at: now,
    anonymized_at: now,
    // Preserve business-critical fields:
    // - date_of_birth: Keep for analytics (as requested)
    // - gender: Keep for analytics
    // - membership_number: Keep for audit trail
    // - whatsapp_number: Keep for business purposes
    // NOTE: do NOT keep auth_user_id linked. The real deletion path
    // (delete-member Edge Function) detaches it (sets NULL) and hard-deletes the
    // auth.users row so the email is freed for re-registration and no PII lingers.
  };
}

/**
 * Checks if a member is soft deleted
 * Uses !! to handle both null and undefined values correctly
 * (undefined !== null evaluates to true, which would incorrectly flag members as deleted)
 */
export function isMemberDeleted(member: Member): boolean {
  return !!member.deleted_at;
}

/**
 * Checks if a member is anonymized
 * Uses !! to handle both null and undefined values correctly
 */
export function isMemberAnonymized(member: Member): boolean {
  return !!member.anonymized_at;
}

/**
 * Gets display name for member (handles deleted members)
 */
export function getMemberDisplayName(member: Member): string {
  if (isMemberDeleted(member)) {
    return 'Deleted Member';
  }
  return `${member.first_name} ${member.last_name}`;
}

/**
 * Gets display email for member (handles deleted members)
 */
export function getMemberDisplayEmail(member: Member): string {
  if (isMemberDeleted(member)) {
    return 'deleted_member@deleted.local'; // Show generic email for UI
  }
  return member.email;
}

/**
 * Sends a deletion confirmation email to a member before anonymization
 */
async function sendMemberDeletionEmail(memberId: string): Promise<boolean> {
  try {
    const { data, error } = await invokeEdgeFunction('send-member-deletion-email', {
      body: { member_id: memberId }
    });

    if (error) {
      logger.warn(`Failed to send deletion email for member ${memberId}:`, error);
      return false;
    }

    logger.info(`Deletion email sent successfully for member ${memberId}:`, data);
    return true;
  } catch (error) {
    logger.warn(`Error sending deletion email for member ${memberId}:`, error);
    return false;
  }
}

/**
 * @deprecated Not wired to any UI — member deletion goes through the `delete-member`
 * Edge Function (via `useSoftDeleteMember` / `useBulkMemberOperations`). This client-side
 * path canNOT remove the backing auth.users record (that needs the service role), so using
 * it would orphan the auth user and block the person from ever re-registering in hok-mobile.
 * Do not re-wire this for deletion; call the Edge Function instead.
 *
 * Soft deletes a member by anonymizing their PII and deleting all photos.
 *
 * Operation order is designed to prevent race conditions:
 * 1. Send deletion email (before anonymization, while member data exists)
 * 2. Delete waitlist entries (BEFORE cancelling bookings to prevent deleted member from being promoted)
 * 3. Anonymize member + delete photos (parallel) - critical operations
 * 4. Cancel future bookings (after anonymization - if this fails, member is already properly deleted)
 * 5. Trigger waitlist promotions (awaited for proper error tracking)
 */
export async function softDeleteMember(memberId: string): Promise<void> {
  // Generate unique email using member ID to avoid duplicate key violations
  const uniqueDeletedEmail = `deleted_member_${memberId}@deleted.local`;

  const anonymizedData = {
    first_name: 'Deleted',
    last_name: 'Member',
    email: uniqueDeletedEmail,
    deleted_at: new Date().toISOString(),
    anonymized_at: new Date().toISOString(),
  };

  // Step 1: Send deletion email BEFORE anonymization (sequential, not parallel)
  let emailSent = false;
  try {
    emailSent = await sendMemberDeletionEmail(memberId);
    if (emailSent) {
      logger.info(`Deletion email sent successfully for member ${memberId}`);
    } else {
      logger.warn(`Deletion email sending failed for member ${memberId}`);
    }
  } catch (error) {
    logger.warn(`Failed to send deletion email for member ${memberId}:`, error);
  }

  // Step 2: Delete waitlist entries FIRST (before cancelling bookings)
  // This prevents the deleted member from being promoted via auto-book-waitlist
  try {
    const { error: waitlistError } = await supabase
      .from('waitlists')
      .delete()
      .eq('member_id', memberId);

    if (waitlistError) {
      logger.warn(`Failed to remove waitlist entries for member ${memberId}:`, waitlistError);
    } else {
      logger.info(`Removed waitlist entries for member ${memberId}`);
    }
  } catch (error) {
    logger.warn(`Error removing waitlist entries for member ${memberId}:`, error);
  }

  // Step 3: Anonymize member and delete photos + files in parallel (critical operations)
  const [memberUpdateResult, photoDeleteResult, fileDeleteResult] = await Promise.allSettled([
    supabase
      .from('members')
      .update(anonymizedData)
      .eq('id', memberId),
    deleteMemberPhotosCompletely(memberId),
    deleteMemberFilesCompletely(memberId)
  ]);

  // Check member anonymization result (critical - must succeed)
  if (memberUpdateResult.status === 'rejected') {
    throw new Error(`Failed to soft delete member: ${memberUpdateResult.reason}`);
  }

  if (memberUpdateResult.value.error) {
    throw new Error(`Failed to soft delete member: ${memberUpdateResult.value.error.message}`);
  }

  // Check photo deletion result (non-critical - log but don't fail)
  if (photoDeleteResult.status === 'rejected') {
    logger.warn(`Failed to delete photos for member ${memberId}:`, photoDeleteResult.reason);
  } else if (photoDeleteResult.value && photoDeleteResult.value.errors && photoDeleteResult.value.errors.length > 0) {
    logger.warn(`Some photos failed to delete for member ${memberId}:`, photoDeleteResult.value.errors);
  }

  // Check file deletion result (non-critical - log but don't fail)
  if (fileDeleteResult.status === 'rejected') {
    logger.warn(`Failed to delete files for member ${memberId}:`, fileDeleteResult.reason);
  } else if (fileDeleteResult.value && fileDeleteResult.value.errors && fileDeleteResult.value.errors.length > 0) {
    logger.warn(`Some files failed to delete for member ${memberId}:`, fileDeleteResult.value.errors);
  }

  // Step 4: Cancel future class bookings (after anonymization)
  // Even if this fails, the member is already properly anonymized
  let classesWithWaitlist: string[] = [];
  try {
    const { data: cancellationResult, error: cancellationError } = await supabase
      .rpc('cancel_future_class_bookings', { p_member_id: memberId });

    if (cancellationError) {
      logger.warn(`Failed to cancel future bookings for member ${memberId}:`, cancellationError);
    } else if (cancellationResult?.[0]) {
      const { cancelled_count, classes_with_waitlist } = cancellationResult[0];
      logger.info(`Cancelled ${cancelled_count} future bookings for member ${memberId}`);
      classesWithWaitlist = classes_with_waitlist || [];
    }
  } catch (error) {
    logger.warn(`Error cancelling future bookings for member ${memberId}:`, error);
  }

  // Step 5: Trigger waitlist auto-promotion for affected classes (awaited for proper error tracking)
  if (classesWithWaitlist.length > 0) {
    const promotionResults = await Promise.allSettled(
      classesWithWaitlist.map(classId =>
        invokeEdgeFunction('auto-book-waitlist', { body: { class_id: classId } })
      )
    );

    // Log any failures for monitoring
    promotionResults.forEach((result, index) => {
      if (result.status === 'rejected') {
        logger.error(`Failed to promote waitlist for class ${classesWithWaitlist[index]}:`, result.reason);
      } else if (result.value?.error) {
        logger.error(`Waitlist promotion error for class ${classesWithWaitlist[index]}:`, result.value.error);
      } else {
        logger.info(`Waitlist promotion triggered for class ${classesWithWaitlist[index]}`);
      }
    });
  }
}

/**
 * Gets count of records that will be affected by member deletion
 * This is for the deletion impact preview
 */
export async function getMemberDeletionImpact(memberId: string) {
  // Execute all queries in parallel for performance
  const [
    checkInsResult,
    classBookingsResult,
    membershipsResult,
    photosResult,
    filesResult,
    activityLogsResult,
    privacySettingsResult,
  ] = await Promise.all([
    supabase
      .from('check_ins')
      .select('id', { count: 'exact', head: true })
      .eq('member_id', memberId),

    supabase
      .from('class_bookings')
      .select('id', { count: 'exact', head: true })
      .eq('member_id', memberId),

    supabase
      .from('memberships')
      .select('id', { count: 'exact', head: true })
      .eq('member_id', memberId),

    supabase
      .from('member_webcam_photos')
      .select('id', { count: 'exact', head: true })
      .eq('member_id', memberId),

    supabase
      .from('member_files')
      .select('id', { count: 'exact', head: true })
      .eq('member_id', memberId),

    supabase
      .from('activity_logs')
      .select('id', { count: 'exact', head: true })
      .eq('member_id', memberId),

    supabase
      .from('member_privacy_settings')
      .select('member_id', { count: 'exact', head: true })
      .eq('member_id', memberId),
  ]);

  return {
    checkIns: checkInsResult.count || 0,
    classBookings: classBookingsResult.count || 0,
    memberships: membershipsResult.count || 0,
    photos: photosResult.count || 0,
    files: filesResult.count || 0,
    activityLogs: activityLogsResult.count || 0,
    privacySettings: privacySettingsResult.count || 0,
    total: (checkInsResult.count || 0) +
           (classBookingsResult.count || 0) +
           (membershipsResult.count || 0) +
           (photosResult.count || 0) +
           (filesResult.count || 0) +
           (activityLogsResult.count || 0) +
           (privacySettingsResult.count || 0),
  };
}
